import { Tooltip } from "bootstrap";
import { apiFetch } from "./api";
import { logVerbose } from "./config";

/**
 * Create an ordered and filtered array of headers
 * @param {string[]} headers - The raw column names
 * @param {string[]} ordered - Column names that must appear first (in this order)
 * @param {string[]} remove - Column names to exclude entirely
 */
export function formatTableHeader(headers, ordered = [], remove = []) {
  if (typeof ordered === 'string') ordered = [ordered];
  const removeSet = new Set(remove);
  const orderedSet = new Set(ordered);

  // 1. Get the "remainder" (headers not in ordered and not in remove)
  const remainder = headers
    .filter(h => !orderedSet.has(h) && !removeSet.has(h))
    .sort((a, b) => a.localeCompare(b));

  // 2. Combine: [Ordered items that actually exist] + [Alphabetical remainder]
  return [
    ...ordered.filter(h => headers.includes(h)), 
    ...remainder
  ];
}

/**
 * Aligns data object to the provided headers
 * @param {string[]} headers - The ordered table headers
 * @param {object} data - The row data from the API
 */
export function formatTableRow(headers, data) {
  // Use map to create the row in the exact order of headers
  return headers.reduce((row, header) => {
    // If data exists, use it; otherwise default to empty string
    row[header] = data[header] ?? ''; 
    return row;
  }, {});
}

/**
 * Returns the standardized common columns for all portal tables
 * @param {boolean} isAdmin - Whether to show the admin-only actions column
 * @param {boolean} [infoOnly] - Whether to include action buttons
 * @returns {Array<Object>} Array of common column definitions
 */
export const getCommonColumns = (isAdmin, infoOnly = false) => {
  const commonColumns = [
    {
      // Info button (default)
      title: '',
      data: null,
      defaultContent: '',
      orderable: false,
      searchable: false,
      render: '#details',
      width: '25px',
      className: 'dtfc-fixed-end',
    },
  ];
  if (!infoOnly) {
    commonColumns.push(
      {
        // Action buttons (Edit, Delete)
        title: '',
        data: null,
        defaultContent: '',
        orderable: false,
        searchable: false,
        render: '#actions',
        width: '50px',
        visible: isAdmin,
        className: 'dtfc-fixed-end',
      }
    );
  }
  return commonColumns;
};

/**
 * @typedef {Object} TableOverrideOptions
 * @property {Array<Array<number|string>>} [order] - Custom sorting order (e.g., [[1, 'desc']])
 * @property {number} [pageLength] - Number of rows to display per page
 * @property {boolean} [stateSave] - Enable/disable state saving
 * @property {Object} [fixedColumns] - Custom fixed column settings
 */

/**
 * Generates standardized DataTables options with support for overrides.
 * @param {boolean} isAdmin - Implicit permissions for some table views
 * @param {string} entityName - Name of the entity for display messages
 * @param {Array<Object>} columns - DataTable column definitions
 * @param {TableOverrideOptions} [overrides={}] - Optional overrides for default settings
 * @param {import('vue').Ref} [queryParams] - Additional query params
 * @returns {Object} Finalized DataTables options object
 */
export function getTableOptions(isAdmin, entityName, columns, overrides = {}, queryParams) {
  const { serverSide, url, orderBy, mock, ...otherOverrides } = overrides;
  
  const defaults = {
    autoWidth: false,
    order: [[0, 'asc']],
    pageLength: 25,
    layout: {
      topStart: ['info'],
      topEnd: ['pageLength', 'search'],
      bottomStart: null,
      bottomEnd: 'paging',
    },
    responsive: true,
    colReorder: true,
    fixedColumns: { left: 1, right: isAdmin ? 2 : 1 },
    scrollX: true,
    stateSave: true,
    language: {
      loadingRecords: `Loading ${entityName}s...`,
      emptyTable: `No ${entityName} data found matching filters`,
      info: `<b>_TOTAL_</b> ${entityName}s total (showing _START_ to _END_)`,
      infoFiltered: `(filtered from _MAX_)`,
      lengthMenu: `_MENU_`,
    },
    
    initComplete: function() {
      const api = this.api();
      // Apply formatting for headers with custom tooltips (once)
      columns.forEach((col, idx) => {
        if (col.tooltip) {
          const th = api.column(idx).header();
          th.innerHTML = `
            <div class="d-flex align-items-center justify-content-center" 
                style="cursor: help; white-space: nowrap;">
              <span data-bs-toggle="tooltip" title="${col.tooltip}">
                ${col.title}
                <i class="bi bi-info-circle ms-1 text-primary" 
                    style="font-size: 0.85rem;">
                </i>
              </span>
            </div>
          `;
          // const trigger = th.querySelector('[data-bs-toggle="tooltip"]');
          // if (trigger) new Tooltip(trigger); 
        }
      });
    },
    
    drawCallback: function(settings) {
      const tag = '[data-bs-toggle="tooltip"]';
      const tableContainer = settings.nTableWrapper;
      const triggers = tableContainer.querySelectorAll(tag);
      triggers.forEach(tip => {
        const instance = Tooltip.getInstance(tip);
        if (instance) {
          instance.dispose();
        }
        new Tooltip(tip);
      });
      // Cleanup any orphaned DOM nodes
      document.querySelectorAll('.tooltip').forEach(node => node.remove());
    },
  };

  if (serverSide && url) {
    const conflicts = ['order', 'stateSave'];
    for (const c of conflicts) {
      delete defaults[c];
      if (c in otherOverrides) delete otherOverrides[c];
    }
    Object.assign(defaults, {
      serverSide: true,
      processing: true,
      search: { return: true },
      ajax: async (data, callback, settings) => {
        // 'data' contains DataTables' current state:
        // start (offset), length (page size), search.value, draw, etc.
        try {
          const draw = data.draw;
          let response;
          if (mock) {
            response = {};
            ({ default: response.items } = await import(/* @vite-ignore */mock)
              .catch(err => console.error(`No mock ${entityName} data found`))
            );
            response.total = response.items.length;
            response.locked = 0;
          } else {
            const colIdx = data.order[0]?.column;
            const colName = columns[colIdx]?.data;
            const tableOrder = { [colName]: data.order[0]?.dir };
            response = await apiFetch(url, {
              method: 'GET',
              // Pass DataTables params as query string
              params: {
                offset: data.start,
                limit: data.length,
                search: data.search.value || undefined,
                // draw: data.draw,
                orderBy: orderBy || tableOrder,
                ...queryParams?.value || {},
              }
            });
          }
          if (logVerbose && response.items) {
            console.groupCollapsed(
              `Received ${response.items.length} ${entityName}s like...`
            );
            console.table(response.items.slice(0, 2));
            console.groupEnd();
          }
          const recordsFiltered = isAdmin 
            ? response.filtered 
            : (response.filtered - (response.locked || 0));
          callback({
            draw: draw, // Echo the draw counter
            recordsTotal: response.total, // Full DB count
            recordsFiltered: recordsFiltered, // Count after search filter
            data: response.items // The actual array of rows
          });
        } catch (err) {
          console.error("Table fetch failed:", err);
          // Inform DataTables to stop the loading spinner
          callback({ data: [] }); 
        }
      },
    });
  }
  // Merge defaults with overrides; overrides will win on duplicate keys
  return { ...defaults, ...otherOverrides };
}

/**
 * Formats a JSON value or JavaScript object into an expandable, syntax-highlighted visual block.
 * @param {any} data - The object, array, or raw value inside the cell field.
 * @returns {string} Safe HTML string containing the expandable JSON layout wrapper.
 */
export function renderExpandableJson(data, type, row) {
  // Gracefully skip execution for missing or completely primitive data records
  if (data === null || data === undefined) {
    return '<span class="text-muted">-</span>';
  }
  if (typeof data !== 'object') return `<span>${data}</span>`;

  try {
    const isArray = Array.isArray(data);
    const totalItems = isArray ? data.length : Object.keys(data).length;
    const tag = row.name ?? (isArray ? 'Array' : 'Object');
    // If the data structure is empty, display a clean placeholder indicator block
    if (totalItems === 0) {
      return `<span class="text-muted">${isArray ? '[]' : '{}'} (Empty)</span>`;
    }

    // Generate a pretty, indented text format
    const prettyJson = JSON.stringify(data, null, 2);
    
    // Escape quotes to prevent raw HTML syntax breakage inside title or element parameters
    const escapedJson = prettyJson.replace(/"/g, '&quot;');
    const previewSummary = isArray 
      ? `[${tag}: ${totalItems} items]` 
      : `{${tag}: ${totalItems} fields}`;
    const preClassParams = `mt-2 p-2 rounded bg-black text-info` +
      ` font-monospace position-relative border border-secondary`;
    return `
      <details class="json-cell-drawer" title="Click to view data structure">
        <summary class="text-primary cursor-pointer font-monospace small">
          <i class="bi bi-chevron-right me-1 json-arrow"></i>${previewSummary}
        </summary>
        <pre class="${preClassParams}"><code>${escapedJson}</code></pre>
      </details>
    `;
  } catch (err) {
    console.error("JSON normalization conversion exception:", err);
    return '<span class="text-danger">[Unparseable Object Block]</span>';
  }
}

export function renderTime(data, type, row) {
  if (!data) return '<span class="text-muted">-</span>';
  if (type !== 'display') return data;

  try {
    const utcParts = data.split('T');
    const utcDate = utcParts[0];
    const utcTime = utcParts[1].slice(0, 8);
    const dateObj = new Date(data);
    // Natively parses to local machine boundaries automatically: "2026-05-20"
    const localDate = dateObj.toLocaleDateString('en-CA');
    // Natively parses to local 24-hour format: "16:03:00"
    const localTime = dateObj.toLocaleTimeString('en-CA', { hour12: false });
    const localZone = dateObj
      .toLocaleDateString('en-CA', { timeZoneName: 'short' })
      .split(', ')[1] || '';
    const toolTipString = `Local Time: ${localDate} ${localTime} ${localZone}`;
    return `
      <span v-tooltip data-bs-toggle="tooltip" title="${toolTipString}">${utcDate} ${utcTime}</span>
    `;
  } catch {
    return data;
  }
}

// import { ref, computed, toRaw } from 'vue';

export function useDataTable(props, emit, tableRef) {
  // Shared string serialization for JSON objects / arrays inside cells
  function cleanValue(value) {
    if (typeof value === 'object' && value !== null) {
      try {
        let tooltip = '';
        if (Array.isArray(value)) {
          tooltip = value.map(obj => {
            if (typeof obj === 'object' && obj !== null) {
              return `{` + Object.entries(obj).map(([k, v]) => ` ${k}: ${v}`) + `}`;
            }
          }).join('\n');
        } else {
          tooltip = Object.entries(value).map(([k, v]) => `${k}: ${v}`).join('\n');
        }
        return `<span title="${tooltip.replace(/"/g, '&quot;')}">${JSON.stringify(value)}</span>`;
      } catch {
        return '[Unserializable Object]';
      }
    }
    return value;
  }

  // Common Column Processor (Used for both local arrays and Server-Side)
  const processColumns = (columns, rawData, inferColumns, excludeColumns) => {
    const allCols = [...(columns ?? [])];
    const definedKeys = new Set(allCols.map(col => col.data));

    if (inferColumns && rawData?.length > 0) {
      const excludeSet = new Set(excludeColumns);
      const allKeys = Array.from(new Set(rawData.flatMap(row => Object.keys(row))))
        .filter(key => !excludeSet.has(key));

      for (const key of allKeys) {
        if (!definedKeys.has(key)) {
          allCols.push({ title: key, data: key, className: 'td-truncate' });
        }
      }
    }

    return allCols.map(col => ({
      ...col,
      render: (data, type, row, meta) => {
        let value = cleanValue(row[col.data]) ?? '';
        const dataId = col.dataId ? row[col.dataId] : `row${meta.row}`;

        switch (col.editType) {
          case 'checkbox':
            return `<input type="checkbox" ${value ? 'checked' : ''} data-id="${dataId}" data-col="${col.data}" />`;
          case 'text':
            return `<input type="text" value="${value}" data-id="${dataId}" data-col="${col.data}" />`;
          case 'select': {
            const selectOptions = (col.options || [])
              .map(opt => `<option value="${opt}" ${value === opt ? 'selected' : ''}>${opt}</option>`)
              .join('');
            return `<select data-id="${dataId}" data-col="${col.data}" class="form-select form-select-sm bg-dark text-white border-secondary">${selectOptions}</select>`;
          }
          default:
            return value;
        }
      }
    }));
  };

  // Event Delegation Manager
  const attachDelegatedListeners = (tableElement, currentDataArray) => {
    if (!tableElement) return;
    const tbody = tableElement.querySelector('tbody');

    tbody?.addEventListener('click', (e) => {
      const tr = e.target.closest('tr');
      if (tr && tableRef.value?.dt) {
        emit('row-click', tableRef.value.dt.row(tr).data());
      }
    }, { passive: true });

    tableElement.addEventListener('change', (e) => {
      const target = e.target;
      if (['INPUT', 'SELECT'].includes(target.tagName) && tableRef.value?.dt) {
        const rowData = tableRef.value.dt.row(target.closest('tr')).data();
        const newValue = target.type === 'checkbox' ? target.checked : target.value;

        // Map back updates locally
        const updatedData = currentDataArray.map(row => {
          if (row.id === rowData.id) return { ...row, [target.dataset.col]: newValue };
          return row;
        });
        emit('update:data', updatedData);
      }
    }, { passive: true });
  };

  return {
    cleanValue,
    processColumns,
    attachDelegatedListeners
  };
}
